import { useEffect, useState } from "react";

const fmt = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");

  return `${h}:${m}:${sec}`;
};

const toMs = (v?: string | null) => (v ? new Date(v).getTime() : Number.NaN);

// Timer window for a cause row. Prefers judge case start/end (caseStartAt /
// caseEndAt); falls back to stream times for logs written before those existed.
export const getHearingTimes = (cause: any) => {
  const caseStart = toMs(cause?.caseStartAt);
  if (Number.isNaN(caseStart)) {
    return { startAt: cause?.streamStartAt ?? null, endAt: cause?.streamEndAt ?? null };
  }
  // Ends from an earlier run (before "Start again") don't count
  const after = (v?: string | null) => (toMs(v) >= caseStart ? v : null);
  const endAt =
    after(cause?.caseEndAt) ??
    (cause?.hearingStatus !== "Active" ? after(cause?.streamEndAt) : null);
  return { startAt: cause.caseStartAt as string, endAt };
};

interface Props {
  cnr: string;
  startAt?: string | null;
  endAt?: string | null;
  videoDuration?: string | null;
  isActive: boolean;
  serverOffsetMs: number;
  className?: string;
}

type ReadonlyProps = Readonly<Props>;

export default function HearingTimer({
  startAt,
  endAt,
  videoDuration,
  isActive,
  serverOffsetMs,
  className = "",
}: ReadonlyProps) {
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  const running = isActive && !!startAt && !endAt;

  useEffect(() => {
    if (!running) return;

    const id = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(id);
  }, [running]);


  // 1. Running timer
  if (running && startAt) {
    const serverNow = currentTime + serverOffsetMs;

    const elapsed = (serverNow - new Date(startAt).getTime()) / 1000;

    return (
      <span className={`font-bold tabular-nums text-red-700 ${className}`}>
        ⏱ {fmt(elapsed)}
      </span>
    );
  }

  // 2. Completed timer — case duration; stream video length as fallback
  if (endAt && startAt) {
    const took = (toMs(endAt) - toMs(startAt)) / 1000;
    if (took >= 0) {
      return (
        <span
          className={`font-mono font-semibold tabular-nums text-gray-500 ${className}`}
        >
          ⏱ {fmt(took)}
        </span>
      );
    }
  }

  if (endAt && videoDuration) {
    return (
      <span
        className={`font-mono font-semibold tabular-nums text-gray-500 ${className}`}
      >
        ⏱ {videoDuration}
      </span>
    );
  }

  return null;
}
